import { useMe } from "@/api/services/main/auth";
import { IconClose } from "@/assets/icons";
import { IconChat } from "@/assets/icons/IconChat";
import { ChatBox } from "@/features/chat/components/ChatBox";
import { useChat } from "@/features/chat/providers/ChatProvider";
import { useVisible } from "@/hooks/useVisible";
import { background, state } from "@/theme/colors";
import { FCC } from "@/types/common";
import { getQueryFromURL } from "@/utils/url";
import { Box, Button, Stack, styled } from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

type TChatProps = {};

export const Chat: FCC<TChatProps> = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = getQueryFromURL();
  const chatPopup = useVisible(!!query && "chat" in query);
  const { currentRoom } = useChat();
  const [unread, setUnread] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 10, y: 10 });
  const dragRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0 });
  const startMousePos = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const { data: meResult } = useMe();

  useEffect(() => {
    const mergedMessages = currentRoom.isFetching
      ? currentRoom.oldMessages
      : [
          ...currentRoom.oldMessages,
          ...currentRoom.newMessages.filter((newMsg) => !currentRoom.oldMessages.some((oldMsg) => oldMsg.id === newMsg.id)),
        ];

    const latestMessage = mergedMessages?.filter((msg) => msg.position === "left").at(-1);

    if (
      !chatPopup.visible &&
      !!meResult &&
      latestMessage &&
      !latestMessage?.readUserIds.some((id) => id.toString() === meResult.data.id.toString())
    ) {
      setUnread(true);
    } else {
      setUnread(false);
    }
  }, [currentRoom.oldMessages, currentRoom.newMessages]);

  const toggleChat = () => {
    if (isDragging || hasDragged.current) return;
    chatPopup.toggle();
    const newSearchParams = new URLSearchParams(searchParams);
    if (unread) setUnread(false);
    if (chatPopup.visible) {
      newSearchParams.delete("chat");
    } else {
      newSearchParams.set("chat", "open");
    }
    setSearchParams(newSearchParams);
  };

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      hasDragged.current = false;
      startPos.current = { x: position.x, y: position.y };
      startMousePos.current = { x: e.clientX, y: e.clientY };
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - startMousePos.current.x;
      const deltaY = e.clientY - startMousePos.current.y;
      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDragged.current = true;
      }
      const newRight = Math.max(0, Math.min(window.innerWidth - 64, startPos.current.x - deltaX));
      const newBottom = Math.max(-20, Math.min(window.innerHeight - 84, startPos.current.y - deltaY));
      setPosition({ x: newRight, y: newBottom });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <ChatWrapper
      ref={dragRef}
      style={{
        right: `${position.x}px`,
        bottom: `${position.y}px`,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      <ChatBox open={chatPopup.visible} />
      <ChatButton open={chatPopup.visible} onClick={toggleChat} onMouseDown={handleMouseDown} isDragging={isDragging}>
        {unread && <UnreadCircle />}
        <Box>{chatPopup.visible ? <IconClose width={32} height={32} color="white" /> : <IconChat width={24} height={24} />}</Box>
      </ChatButton>
    </ChatWrapper>
  );
};

const ChatWrapper = styled(Stack)`
  position: fixed;
  z-index: 1300;
  transition: none;
`;

const UnreadCircle = styled(Box)`
  position: absolute;
  right: 0;
  top: 8px;
  width: 14px;
  height: 14px;
  border-radius: 100px;
  background-color: ${state.error_1};
  border: 0.5px solid white;
`;

const ChatButton = styled(Button)<{ open: boolean; isDragging?: boolean }>`
  width: 64px;
  height: 64px;
  transform: ${({ open }) => (open ? "rotate(45deg)" : "rotate(0deg)")};
  transition: ${({ isDragging }) => (isDragging ? "none" : "transform 0.3s ease, background-color 0.3s ease")};
  background-color: ${background.main};
  border-radius: 100px;
  border-width: 0px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16) !important;
  cursor: ${({ isDragging }) => (isDragging ? "grabbing" : "grab")};
  padding: 0px;
  user-select: none;
  &:hover {
    background-color: ${background.main};
  }

  &:active {
    cursor: grabbing;
  }

  > div {
    display: flex;
    justify-content: center;
    align-items: baseline;
    transform: ${({ open }) => (open ? "rotateZ(45deg)" : "0")};
    pointer-events: none;
  }
`;
